/**
 * A qué anchura estamos.
 *
 * POR QUÉ EN JAVASCRIPT Y NO SOLO EN CSS: hasta 720px las tablas de esta aplicación no se
 * encogen, se CONVIERTEN en fichas con menos datos. Eso no es un cambio de estilo, es otro
 * contenido —tres cifras en vez de nueve—, y resolverlo pintando las dos versiones y ocultando
 * una con `display: none` duplicaría cada fila en el árbol de accesibilidad: un lector de
 * pantalla leería la lista dos veces.
 *
 * Se usa `matchMedia` y no `resize`: el navegador solo avisa cuando se CRUZA el umbral, no en
 * cada píxel de un arrastre.
 *
 * Los umbrales coinciden con los del CSS. Si se tocan aquí hay que tocarlos allí.
 */
import { useEffect, useState } from 'react';

export const BREAKPOINTS = {
  /** Por debajo: fichas apiladas. */
  movil: 720,
  /** Por debajo: tabla reducida. Por encima: todas las columnas. */
  ancho: 1100,
} as const;

export type Breakpoint = 'movil' | 'tablet' | 'ancho';

function medir(): Breakpoint {
  // En un entorno sin ventana (pruebas) se asume la anchura completa.
  if (typeof window === 'undefined') return 'ancho';
  if (window.matchMedia(`(min-width: ${BREAKPOINTS.ancho}px)`).matches) return 'ancho';
  if (window.matchMedia(`(min-width: ${BREAKPOINTS.movil}px)`).matches) return 'tablet';
  return 'movil';
}

export function useBreakpoint(): Breakpoint {
  const [valor, setValor] = useState<Breakpoint>(medir);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const consultas = [
      window.matchMedia(`(min-width: ${BREAKPOINTS.movil}px)`),
      window.matchMedia(`(min-width: ${BREAKPOINTS.ancho}px)`),
    ];
    const alCambiar = () => setValor(medir());
    // Se vuelve a medir al montar: entre el primer render y este efecto ha podido girar el móvil.
    alCambiar();
    for (const consulta of consultas) consulta.addEventListener('change', alCambiar);
    return () => {
      for (const consulta of consultas) consulta.removeEventListener('change', alCambiar);
    };
  }, []);

  return valor;
}
